import React, { useState, useCallback } from 'react';
import { Plus, Check, Flame, Leaf, Star, Ban } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import NoImageTile from '@/components/NoImageTile';

/**
 * MenuItemCard - Premium menu item card for kiosk display
 * 
 * Features:
 * - Large image (lazy loaded)
 * - Name + price clearly visible
 * - Optional tags: Popular, Spicy, Vegetarian, Sold Out
 * - Add button or tap to open detail modal
 * - Skeleton loading state
 */
export default function MenuItemCard({
  item,
  onAdd,
  onOpenDetail,
  isAdding = false,
  justAdded = false,
  className = '',
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const {
    id,
    name,
    description,
    price,
    image_url,
    is_available = true,
    is_popular,
    is_spicy,
    is_vegetarian,
    modifier_groups = [],
  } = item;

  const hasModifiers = modifier_groups && modifier_groups.length > 0;
  const isSoldOut = !is_available;

  const handleClick = useCallback(() => {
    if (isSoldOut) return;
    
    if (hasModifiers) {
      // Open item detail modal for modifier selection
      onOpenDetail?.(item);
    } else {
      // Quick add to cart
      onAdd?.(item);
    }
  }, [item, hasModifiers, isSoldOut, onAdd, onOpenDetail]);

  const handleAddClick = useCallback((e) => {
    e.stopPropagation();
    if (isSoldOut) return;
    
    if (hasModifiers) {
      onOpenDetail?.(item);
    } else {
      onAdd?.(item);
    }
  }, [item, hasModifiers, isSoldOut, onAdd, onOpenDetail]);

  const hasPhoto = !!image_url && !imageError;

  return (
    <div
      onClick={handleClick}
      className={`
        group relative
        bg-[#1a1f26] border border-white/10
        rounded-2xl overflow-hidden
        transition-all duration-200
        ${isSoldOut 
          ? 'opacity-60 cursor-not-allowed' 
          : 'cursor-pointer hover:border-white/20 hover:shadow-xl hover:shadow-black/30 active:scale-[0.98]'
        }
        ${justAdded ? 'ring-2 ring-[#10b981] ring-offset-2 ring-offset-[#0f1419]' : ''}
        ${className}
      `}
      role="button"
      tabIndex={isSoldOut ? -1 : 0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      aria-label={`${name} - ${formatCurrency(price)}${isSoldOut ? ' - Sold Out' : ''}`}
      aria-disabled={isSoldOut}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] bg-[#252b33] overflow-hidden">
        {/* Skeleton while loading */}
        {hasPhoto && !imageLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-[#252b33] via-[#2d343d] to-[#252b33]" />
        )}

        {/* Image: the item's own photo, or a neutral tile */}
        {hasPhoto ? (
        <img
          src={image_url}
          alt={name}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
          className={`
            w-full h-full object-cover
            transition-all duration-300
            ${imageLoaded ? 'opacity-100' : 'opacity-0'}
            ${!isSoldOut ? 'group-hover:scale-105' : ''}
          `}
        />
        ) : (
          <NoImageTile name={name} dark className="absolute inset-0 w-full h-full" />
        )}

        {/* Sold Out Overlay */}
        {isSoldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#ef4444] text-white rounded-full">
              <Ban size={18} />
              <span className="font-semibold">Sold Out</span>
            </div>
          </div>
        )}

        {/* Tags */}
        {!isSoldOut && (is_popular || is_spicy || is_vegetarian) && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {is_popular && (
              <span className="flex items-center gap-1 px-2 py-1 bg-[#f5a623] text-[#0f1419] text-xs font-semibold rounded-full">
                <Star size={12} fill="currentColor" />
                Popular
              </span>
            )}
            {is_spicy && (
              <span className="flex items-center gap-1 px-2 py-1 bg-[#ef4444] text-white text-xs font-semibold rounded-full">
                <Flame size={12} />
                Spicy
              </span>
            )}
            {is_vegetarian && (
              <span className="flex items-center gap-1 px-2 py-1 bg-[#10b981] text-white text-xs font-semibold rounded-full">
                <Leaf size={12} />
                Veggie
              </span>
            )}
          </div>
        )}

        {/* Quick Add Button */}
        {!isSoldOut && (
          <button
            onClick={handleAddClick}
            disabled={isAdding}
            className={`
              absolute bottom-3 right-3
              w-12 h-12 rounded-full
              flex items-center justify-center
              transition-all duration-200
              shadow-lg
              ${justAdded
                ? 'bg-[#10b981] text-white'
                : 'bg-[#f5a623] text-[#0f1419] hover:brightness-110 hover:scale-110 active:scale-95'
              }
              ${isAdding ? 'animate-pulse' : ''}
            `}
            aria-label={hasModifiers ? `Customize ${name}` : `Add ${name} to cart`}
          >
            {justAdded ? (
              <Check size={24} strokeWidth={3} />
            ) : (
              <Plus size={24} strokeWidth={2.5} />
            )}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Name */}
        <h3 className="font-semibold text-white text-lg leading-tight line-clamp-2 min-h-[3.5rem]">
          {name}
        </h3>

        {/* Description (truncated) */}
        {description && (
          <p className="mt-1 text-sm text-gray-400 line-clamp-2">
            {description}
          </p>
        )}

        {/* Price */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xl font-bold text-[#f5a623]">
            {formatCurrency(price)}
          </span>
          
          {/* Modifier indicator */}
          {hasModifiers && !isSoldOut && (
            <span className="text-xs text-gray-500">
              Customizable
            </span>
          )}
        </div>
      </div>

      {/* Added Animation Overlay */}
      {justAdded && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[#10b981]/10 animate-pulse" />
        </div>
      )}
    </div>
  );
}

/**
 * MenuItemCardSkeleton - Loading skeleton for menu item cards
 */
export function MenuItemCardSkeleton({ className = '' }) {
  return (
    <div className={`bg-[#1a1f26] border border-white/10 rounded-2xl overflow-hidden ${className}`}>
      {/* Image skeleton */}
      <div className="aspect-[4/3] bg-[#252b33] animate-pulse" />
      
      {/* Content skeleton */}
      <div className="p-4 space-y-3">
        <div className="h-6 bg-[#252b33] rounded animate-pulse w-3/4" />
        <div className="h-4 bg-[#252b33] rounded animate-pulse w-full" />
        <div className="h-4 bg-[#252b33] rounded animate-pulse w-1/2" />
        <div className="h-7 bg-[#252b33] rounded animate-pulse w-1/3 mt-2" />
      </div>
    </div>
  );
}
